import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { BrowserRouter as Router } from 'react-router-dom'
import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { BottomSheetProvider } from './state-management/contexts/BottomSheetContext'
import { ToastProvider } from './state-management/contexts/ToastContext'
import { AppBarScrimProvider } from './state-management/contexts/AppBarScrimContext'
import { AppMetaContentProvider } from './state-management/providers/AppMetaContentProvider'
import { LoadingProvider } from './state-management/providers/LoadingProvider'
import { ESignLogMessage, isDevEnvironment, logESignEvent, markWebviewBoot } from './utils/utils'
import { GlobalToast } from './components/ui-components/GlobalToast'
import { OverlayLoadingPage } from './components/ui-components/loading/OverlayLoadingPage'
import { SafeAreaOverlays } from './components/ui-components/safe-area/SafeAreaOverlays'
import { AllRoutes } from './Routes/AllRoutes'
import { StackProvider } from './stack-navigation/StackProvider'

function FallbackComponent() {
  useEffect(() => {
    // beforeCapture already stored the error. This covers a boundary that skipped it.
    // Child effects run before App, so `failed` is set before `ready`.
    markWebviewBoot('failed', { message: 'webview_failed_in_app' })
  }, [])

  return <div>An error has occurred</div>
}

const myFallback = <FallbackComponent />

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
      retry: false
    }
  }
})

export default function App() {
  useEffect(() => {
    // @ts-ignore
    window['invalidateQueries'] = function () {
      queryClient.invalidateQueries()
    }

    // Critical: Log when App component mounts
    logESignEvent(ESignLogMessage.APP_COMPONENT_MOUNTED)
    // Boot done. index.html reports nothing once this is set and #root has content.
    markWebviewBoot('ready')
  }, [])

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(_scope, error) => {
        markWebviewBoot('failed', { message: 'webview_failed_in_app', error })
      }}
      fallback={myFallback}
    >
      <div className='h-screen w-full'>
        <AppMetaContentProvider>
          <LoadingProvider>
            <ToastProvider>
              <QueryClientProvider client={queryClient}>
                <Router>
                  <StackProvider>
                    <BottomSheetProvider>
                      <AppBarScrimProvider>
                        <AllRoutes />
                        {isDevEnvironment() && <SafeAreaOverlays />}
                      </AppBarScrimProvider>
                    </BottomSheetProvider>
                    <GlobalToast />
                    <OverlayLoadingPage />
                  </StackProvider>
                </Router>
              </QueryClientProvider>
            </ToastProvider>
          </LoadingProvider>
        </AppMetaContentProvider>
      </div>
    </Sentry.ErrorBoundary>
  )
}
